"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import { claimNonCustodialAction } from "@/app/actions";

type RosterEntry = { email: string; name: string; role: "admin" | "signer" };

/**
 * One-time non-custodial claim: defines who signs payouts (roster),
 * how many approvals are needed (threshold), and on which chains.
 * Required before any wallet can be created for the customer.
 */
export default function WalletClaim({ customerId }: { customerId: string }) {
  const router = useRouter();
  const [pending, start] = useTransition();
  const [roster, setRoster] = useState<RosterEntry[]>([
    { email: "", name: "", role: "admin" },
    { email: "", name: "", role: "admin" },
  ]);
  const [threshold, setThreshold] = useState("1");
  const [error, setError] = useState<string | null>(null);
  const [claimed, setClaimed] = useState(false);
  const [claimId, setClaimId] = useState<string | null>(null);

  function setEntry(i: number, patch: Partial<RosterEntry>) {
    setRoster((r) => r.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  }

  function submit() {
    setError(null);
    start(async () => {
      // `chains` deliberately omitted: Conduit auto-provisions every supported
      // chain, and this choice is a one-shot — a partial claim (e.g. base-only)
      // can't be re-claimed later and leaves other chains needing re-auth.
      const res = await claimNonCustodialAction(customerId, {
        roster: roster
          .filter((e) => e.email)
          .map((e) => ({
            email: e.email,
            ...(e.name ? { name: e.name } : {}),
            role: e.role,
            credentialType: "passkey" as const,
          })),
        signingThreshold: Number(threshold),
      });
      if (res.ok) {
        setClaimed(true);
        setClaimId((res.data as { claimId?: string })?.claimId ?? null);
        router.refresh();
      } else setError(res.error);
    });
  }

  const validEmails = roster.filter((e) => e.email).length;
  const admins = roster.filter((e) => e.email && e.role === "admin").length;

  if (claimed) {
    return (
      <div className="card-green p-6">
        <div className="display text-[22px]">Wallet account provisioning</div>
        <p className="mt-1 text-[14px] font-light">
          Conduit is provisioning the wallet account — this can take several minutes. When it completes, each signer
          appears on the Permissions page with a passkey enrollment link you can copy and share. Once the roster is
          active, create wallets below.
        </p>
        {claimId && (
          <p className="mono mt-2 text-[12px]">
            claim {claimId} — keep this ID for support if provisioning stalls
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="card p-6">
      <div className="mb-1 flex items-center gap-2">
        <ShieldCheck size={18} />
        <h3 className="display text-[20px]">Set up wallet signing</h3>
      </div>
      <p className="mb-5 max-w-xl text-[13px] font-light" style={{ color: "var(--c-ink-soft)" }}>
        Your wallets are non-custodial: your team holds the keys. Name at least two admins (they can manage the
        roster) and choose how many approvals a payout needs. Each signer enrolls a passkey via an email link.
      </p>

      <div className="flex flex-col gap-3">
        {roster.map((e, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className="field"
              type="email"
              placeholder="email@company.com"
              value={e.email}
              onChange={(ev) => setEntry(i, { email: ev.target.value })}
            />
            <input
              className="field max-w-[160px]"
              placeholder="Name"
              value={e.name}
              onChange={(ev) => setEntry(i, { name: ev.target.value })}
            />
            <select
              className="field max-w-[110px]"
              value={e.role}
              onChange={(ev) => setEntry(i, { role: ev.target.value as "admin" | "signer" })}
            >
              <option value="admin">admin</option>
              <option value="signer">signer</option>
            </select>
            {roster.length > 2 && (
              <button className="btn btn-ghost !p-2" onClick={() => setRoster((r) => r.filter((_, j) => j !== i))}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        className="btn btn-ghost mt-3 !px-4 !py-1.5 text-[13px]"
        onClick={() => setRoster((r) => [...r, { email: "", name: "", role: "signer" }])}
      >
        <Plus size={13} /> Add signer
      </button>

      <div className="mt-5 flex flex-wrap items-end gap-6">
        <div>
          <label className="label">Approvals required (quorum)</label>
          <input
            className="field w-[90px] text-center"
            inputMode="numeric"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
        </div>
        <div className="max-w-xs">
          <label className="label">Chains</label>
          <p className="text-[12px] font-light" style={{ color: "var(--c-ink-soft)" }}>
            All supported chains are provisioned automatically — this can't be widened later without
            re-authentication, so nothing to pick here.
          </p>
        </div>
      </div>

      <button
        className="btn btn-primary mt-6"
        disabled={pending || validEmails < 2 || admins < 2 || !threshold}
        onClick={submit}
      >
        {pending ? "Claiming…" : "Claim wallet control"}
      </button>
      {validEmails >= 2 && admins < 2 && (
        <p className="mt-2 text-[12px]" style={{ color: "var(--c-danger)" }}>
          Conduit requires at least two admins on the roster.
        </p>
      )}
      {error && (
        <div className="card-cream mt-4 p-4 text-[14px] font-light" style={{ color: "var(--c-danger)" }}>
          {error}
        </div>
      )}
    </div>
  );
}
